import React, { Component } from 'react'
import { Link } from 'react-router-dom'
import './FeesPage.css' // the Feepage stylesheet you pasted (adjust filename/path if different)
import './Budget.css' // calculator-only additions
import Header from './layouts/Header'

const SERVICE_RATE = 0.035

const CATEGORIES = [
  { key: 'vvip', priceKey: 'priceOne', name: 'VVIP', note: 'Premium access', placeholder: '550' },
  { key: 'vip', priceKey: 'priceTwo', name: 'VIP', note: 'Premium seating', placeholder: '150' },
  {
    key: 'general',
    priceKey: 'priceThree',
    name: 'General',
    note: 'Standard admission',
    placeholder: '100',
  },
]

const formatRand = (amount) => {
  const [whole, cents] = Number(amount).toFixed(2).split('.')

  return {
    whole: 'R' + whole.replace(/\B(?=(\d{3})+(?!\d))/g, ' '),
    cents,
  }
}

const Money = ({ value, className, negative }) => {
  const { whole, cents } = formatRand(value)

  return (
    <span className={`fp-money ${className || ''}`}>
      {negative ? '−' : ''}
      {whole}
      <span className="fp-money__cents">,{cents}</span>
    </span>
  )
}

class Budget extends Component {
  constructor(props) {
    super(props)

    this.state = {
      vvip: '',
      vip: '',
      general: '',
      priceOne: '',
      priceTwo: '',
      priceThree: '',
      calResults: '',
      result: null,
    }

    this.handleChange = this.handleChange.bind(this)
    this.handleCalculation = this.handleCalculation.bind(this)
  }

  componentDidMount() {
    document.title = 'Karamale - Fee Calculator'
  }

  handleChange(field) {
    return (event) => {
      this.setState({
        [field]: event.target.value,
        calResults: '',
        result: null,
      })
    }
  }

  handleCalculation() {
    for (const cat of CATEGORIES) {
      const qty = this.state[cat.key]
      const price = this.state[cat.priceKey]

      if (qty !== '' && price === '') {
        this.setState({ calResults: `${cat.name} tickets require a ticket price.`, result: null })
        return
      }

      if (qty === '' && price !== '') {
        this.setState({
          calResults: `${cat.name} ticket price requires a number of tickets.`,
          result: null,
        })
        return
      }
    }

    const rows = CATEGORIES.map((cat) => ({
      name: cat.name,
      qty: Number(this.state[cat.key] || 0),
      price: Number(this.state[cat.priceKey] || 0),
    }))
      .filter((row) => row.qty > 0 && row.price > 0)
      .map((row) => ({ ...row, sales: row.qty * row.price }))

    const gross = rows.reduce((sum, row) => sum + row.sales, 0)
    const tickets = rows.reduce((sum, row) => sum + row.qty, 0)

    if (gross <= 0) {
      this.setState({
        calResults: 'Enter at least one ticket category to calculate your fees.',
        result: null,
      })
      return
    }

    const fee = gross * SERVICE_RATE
    const net = gross - fee

    this.setState({
      calResults: '',
      result: { gross, fee, net, rows, tickets },
    })
  }

  render() {
    const { calResults, result } = this.state

    const isLoggedIn =
      localStorage.getItem('businessname') !== null && localStorage.getItem('accounttype') !== null

    const actionButton = isLoggedIn ? (
      <Link
        to={{
          pathname: '/addevent',
          state: { prevpage: 'budget' },
        }}
        className="fp-btn fp-btn--brand"
      >
        Create an event
      </Link>
    ) : (
      <Link to="/organiser-registration" className="fp-btn fp-btn--brand">
        Get started
      </Link>
    )

    const ctaButton = isLoggedIn ? (
      <Link
        to={{
          pathname: '/addevent',
          state: { prevpage: 'budget' },
        }}
        className="fp-btn fp-btn--light"
      >
        Create an event
      </Link>
    ) : (
      <Link to="/organiser-registration" className="fp-btn fp-btn--light">
        Get started
      </Link>
    )

    return (
      <div className="fp-page">
        <Header />

        <main>
          {/* ---------- Hero ---------- */}
          <section className="fp-hero">
            <div className="fp-aurora" aria-hidden="true">
              <span className="fp-aurora__a"></span>
              <span className="fp-aurora__b"></span>
            </div>

            <div className="fp-wrap fp-hero__inner">
              <div>
                <span className="fp-pill">
                  <i></i>Karamale fee calculator
                </span>

                <h1>
                  Know your fees <em>before you sell.</em>
                </h1>

                <p className="fp-hero__sub">
                  Estimate the service fees and your earnings based on your ticket categories,
                  quantities and prices.
                </p>

                <div className="fp-actions">
                  {actionButton}
                  <a href="#calculator" className="fp-btn fp-btn--ghost">
                    Try the calculator
                  </a>
                </div>
              </div>

              <div className="fp-hero__visual">
                <div className="fp-bigcard">
                  <small>Service fee per ticket sold</small>
                  <strong>3.5%</strong>
                  <div className="fp-bigcard__line">Pay only when you sell.</div>
                </div>

                <div className="fp-mini fp-mini--one">
                  <small>Upfront service fee</small>
                  <strong>R0</strong>
                  <span>No sales, no fees.</span>
                </div>

                <div className="fp-mini fp-mini--two">
                  <small>You keep</small>
                  <strong>96.5%</strong>
                  <span>of every ticket sale</span>
                </div>
              </div>
            </div>
          </section>

          {/* ---------- Calculator ---------- */}
          <section className="fp-section" id="calculator">
            <div className="fp-wrap">
              <div className="fp-head">
                <h2 className="fp-h2">
                  Build your <span>estimate.</span>
                </h2>

                <p>
                  Enter the number of tickets you expect to sell and the price of each ticket
                  category. You only need to complete the categories that apply to your event.
                </p>
              </div>

              <div className="fp-calc">
                <div className="fp-panel">
                  <div className="fp-panel__head">
                    <span>Fee calculator</span>
                    <b>
                      <i></i>3.5% per ticket sale
                    </b>
                  </div>

                  <h3 className="fp-calc__title">What are you selling?</h3>

                  {CATEGORIES.map((cat) => (
                    <div className="fp-calc__row" key={cat.key}>
                      <div className="fp-calc__name">
                        <span className="fp-calc__dot"></span>
                        <div>
                          <strong>{cat.name}</strong>
                          <small>{cat.note}</small>
                        </div>
                      </div>

                      <div className="fp-field">
                        <label htmlFor={`fp-${cat.key}`}>Tickets</label>
                        <input
                          id={`fp-${cat.key}`}
                          type="number"
                          min="0"
                          placeholder="0"
                          value={this.state[cat.key]}
                          onChange={this.handleChange(cat.key)}
                        />
                      </div>

                      <div className="fp-field">
                        <label htmlFor={`fp-${cat.priceKey}`}>Price per ticket</label>
                        <div className="fp-field__money">
                          <span>R</span>
                          <input
                            id={`fp-${cat.priceKey}`}
                            type="number"
                            min="0"
                            placeholder={cat.placeholder}
                            value={this.state[cat.priceKey]}
                            onChange={this.handleChange(cat.priceKey)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={this.handleCalculation}
                    className="fp-btn fp-btn--brand fp-calc__submit"
                  >
                    Calculate my fees →
                  </button>

                  {calResults && (
                    <div className="fp-error" role="alert">
                      <small>Check your input</small>
                      <strong>{calResults}</strong>
                    </div>
                  )}

                  {result && (
                    <div className="fp-result">
                      <div className="fp-result__head">
                        <span>Your estimate</span>
                        <span>
                          {result.tickets} {result.tickets === 1 ? 'ticket' : 'tickets'}
                        </span>
                      </div>

                      <div className="fp-result__hero">
                        <small>You keep</small>
                        <Money value={result.net} className="fp-result__amount" />
                      </div>

                      <div className="fp-result__bar" aria-hidden="true">
                        <div className="fp-result__bar-net"></div>
                        <div className="fp-result__bar-fee"></div>
                      </div>

                      <div className="fp-result__legend">
                        <span>
                          <i className="fp-result__dot fp-result__dot--net"></i>
                          96.5% yours
                        </span>
                        <span>
                          <i className="fp-result__dot fp-result__dot--fee"></i>
                          3.5% Karamale
                        </span>
                      </div>

                      <div className="fp-result__lines">
                        {result.rows.map((row) => (
                          <div className="fp-result__line" key={row.name}>
                            <span>
                              {row.name}
                              <small>
                                {row.qty} × <Money value={row.price} />
                              </small>
                            </span>
                            <Money value={row.sales} />
                          </div>
                        ))}

                        <div className="fp-result__line fp-result__line--total">
                          <span>Ticket sales</span>
                          <Money value={result.gross} />
                        </div>

                        <div className="fp-result__line">
                          <span>Service fee (3.5%)</span>
                          <Money value={result.fee} negative />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <aside className="fp-calc__side">
                  <div className="fp-fee fp-fee--hot">
                    <div className="fp-fee__top">
                      <span>Service fee</span>
                      <b>Per sale</b>
                    </div>
                    <strong>3.5%</strong>
                    <p>of each ticket sale. Charged only when a ticket is sold.</p>
                  </div>

                  <div className="fp-fee">
                    <div className="fp-fee__top">
                      <span>Upfront service fee</span>
                      <b>Nothing</b>
                    </div>
                    <strong>R0</strong>
                    <p>
                      No campaign budget to set up. The 3.5% fee is only charged when a ticket is
                      sold. Physical tickets are paid for before they're generated or printed.
                    </p>
                  </div>
                </aside>
              </div>
            </div>
          </section>

          {/* ---------- How the fee works ---------- */}
          <section className="fp-section">
            <div className="fp-wrap">
              <div className="fp-flow">
                <div>
                  <h2 className="fp-h2">
                    How the fee <span>works.</span>
                  </h2>

                  <p className="fp-lead">
                    Karamale takes 3.5% of every ticket sale. You don't pay anything until a
                    customer buys your ticket.
                  </p>

                  <div className="fp-zero">
                    <small>Service fee paid upfront</small>
                    <strong>R0</strong>
                  </div>
                </div>

                <ol className="fp-steps">
                  <li>
                    <span className="fp-steps__n">1</span>
                    <div>
                      <strong>Customer buys</strong>
                      <p>
                        Your customer purchases a ticket through Karamale and receives their digital
                        ticket in their account.
                      </p>
                    </div>
                  </li>

                  <li>
                    <span className="fp-steps__n">2</span>
                    <div>
                      <strong>Fee is taken</strong>
                      <p>
                        The 3.5% service fee is calculated on the ticket sale and deducted from that
                        sale.
                      </p>
                    </div>
                  </li>

                  <li>
                    <span className="fp-steps__n">3</span>
                    <div>
                      <strong>You keep the rest</strong>
                      <p>The remaining ticket revenue belongs to you.</p>
                    </div>
                  </li>
                </ol>
              </div>

              <div className="fp-note">
                <span className="fp-tick"></span>
                <div>
                  <strong>Payouts</strong>
                  <p>A small payout processing fee applies when you request a payout.</p>
                </div>
              </div>
            </div>
          </section>

          {/* ---------- Ticket options ---------- */}
          <section className="fp-section">
            <div className="fp-wrap">
              <div className="fp-head">
                <h2 className="fp-h2">
                  Ticket <span>options.</span>
                </h2>

                <p>
                  Every ticket, digital or physical, carries a QR code and a unique ticket number
                  and is scanned the same way at entry. The only difference is how it's delivered.
                </p>
              </div>

              <div className="fp-cols">
                <div className="fp-fee fp-fee--hot">
                  <div className="fp-fee__top">
                    <span>Online tickets</span>
                    <b>Digital</b>
                  </div>
                  <strong>3.5%</strong>
                  <p>per ticket sale</p>

                  <ul className="fp-checks fp-checks--card">
                    <li>Instant delivery to the customer's Karamale account</li>
                    <li>QR code scanned at entry</li>
                    <li>Unique ticket number</li>
                    <li>Ticket category &amp; admission details</li>
                    <li>Customer ticket history</li>
                  </ul>
                </div>

                <div className="fp-fee">
                  <div className="fp-fee__top">
                    <span>Self-print</span>
                    <b>Physical</b>
                  </div>
                  <strong>R1.00</strong>
                  <p>per ticket, paid online upfront</p>

                  <ul className="fp-checks fp-checks--card">
                    <li>Same QR code &amp; unique ticket number</li>
                    <li>Scanned at entry like any other ticket</li>
                    <li>Pay online before your tickets are generated</li>
                    <li>Download and print them yourself</li>
                  </ul>
                </div>

                <div className="fp-fee">
                  <div className="fp-fee__top">
                    <span>Printed by Karamale</span>
                    <b>Physical</b>
                  </div>
                  <strong>R3.50</strong>
                  <p>per ticket, paid to Karamale upfront</p>

                  <ul className="fp-checks fp-checks--card">
                    <li>Same QR code &amp; unique ticket number</li>
                    <li>Scanned at entry like any other ticket</li>
                    <li>Pay Karamale before printing starts</li>
                    <li>Printed and ready for physical distribution</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* ---------- CTA ---------- */}
          <section className="fp-section fp-section--last">
            <div className="fp-wrap">
              <div className="fp-cta">
                <h2>
                  Plan it. <em>Launch it.</em>
                </h2>
                <p>
                  Sell online and know what you're making. Karamale takes a simple fee only when you
                  make a sale.
                </p>

                <div className="fp-actions fp-actions--center">{ctaButton}</div>
              </div>
            </div>
          </section>
        </main>
      </div>
    )
  }
}

export default Budget
